import { FC, useMemo } from 'react';

import { ColumnTable, Entry } from '@/components/ColumnTable';
import { useMacro } from '@/hooks/dbtStrategy/useMacro';
import { useCommonLineageEntries } from '@/hooks/useCommonLineageEntries';
import { PanelDetails, ResourceDetailsParams } from './panelDetailTypes';
import { TagLinks } from './TagLinks';

export const useMacroDetailsPanel = ({
  uniqueId,
  projectName,
}: ResourceDetailsParams): PanelDetails => {
  const macroQuery = useMacro(uniqueId);

  const commonEntries = useCommonLineageEntries(projectName);

  return useMemo(() => {
    const macro = macroQuery.data.definition;
    if (!macro) {
      return { ...macroQuery, rows: null, description: null };
    }

    const rows: Entry[] = [
      ...commonEntries,
      {
        key: 'Tags',
        data: <TagLinks tags={macro.tags} />,
      },
      {
        key: 'Package',
        data: uniqueId.packageName,
      },
    ];

    return { ...macroQuery, rows, description: macro.description };
  }, [commonEntries, macroQuery, uniqueId.packageName]);
};

export const MacroDetailsTable: FC<ResourceDetailsParams> = ({
  className,
  children,
  compact,
  ...props
}) => {
  const macroDetails = useMacroDetailsPanel(props);

  return (
    <div className={className}>
      {children?.(macroDetails)}
      <ColumnTable
        tableEntries={macroDetails.rows}
        compact={compact}
        isLoading={macroDetails.fetching}
      />
    </div>
  );
};
